import { useEffect, useRef, useState } from "react";
import image from "@assets/images/DietPlanHeader.png";
import BackImg from "@assets/images/stepsBack.jpg";
import {
  StressReduce,
  MovingMore,
  HealthyEat,
  GreenMoon,
  BludDrop,
  Thunder,
  Notes,
  YelloPerson,
  TargetGreen,
  Joystick,
  OrangeApple,
  ReturnDashboard,
} from "@utils/defaultImages";
import { useNavigate } from "react-router-dom";
import { createQuiz } from "api/quizGame";
import { toast } from "react-toastify";
import ButtonLoader from "@components/ui/Loaders/ButtonLoader";
import { NavigationPrompt } from "@utils/NavigationBlocker";
import Timer from "@components/ui/Timer";
import { useTranslation } from "react-i18next";
import { useTimer } from "context/TimeContext";
import ReacreationLoading from "@components/ui/RecreationLoading";
import { useLoaderSlides } from "data/uiData/LoadersData";
import { useUI } from "context/LoadingContext";

const StartQuizGame = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  // const { setIsActionLocked } = useUI();
  const [selectedCategoryLabel, setSelectedCategoryLabel] = useState(null);
  const { t } = useTranslation();
  const isMounted = useRef(false);
  const { quizTimeLeft, startQuizTimer, stopQuizTimer, setQuizTimeLeft } =
    useTimer();
  const mindfulnessSlides = useLoaderSlides("quizGame");

  const nutrientData = [
    {
      label: "Reduce Stress",
      key: "stress",
      icon: <StressReduce className="w-[24px] h-[24px]" />,
      tabColor: "#FFF6F0",
      value: "Learn quick techniques to calm your mind.",
    },
    {
      label: "Eat Healthier",
      key: "eatHealthy",
      icon: <HealthyEat className="w-[24px] h-[24px]" />,
      tabColor: "#F0FEFF",
      value: "Fuel your body with balanced, tasty meals.",
    },
    {
      label: "Moving More",
      key: "moving",
      icon: <MovingMore className="w-[24px] h-[24px]" />,
      tabColor: "#FFF0FB",
      value: "Stay active with workouts that fit your routine.",
    },
    {
      label: "Improve Sleep",
      key: "sleep",
      icon: <GreenMoon className="w-[24px] h-[24px]" />,
      tabColor: "#F0FFF4",
      value: "Build knowledge and habits for better rest.",
    },
    {
      label: "Stay Hydrated",
      key: "hydration",
      icon: <BludDrop className="w-[24px] h-[24px]" />,
      tabColor: "#F0F5FF",
      value: "Learn about hydration benefits and reminders.",
    },
    {
      label: "Boost Energy",
      key: "energy",
      icon: <Thunder className="w-[24px] h-[24px]" />,
      tabColor: "#FFF0F0",
      value: "Focus on foods, workouts, and habits that increase daily energy.",
    },
    {
      label: "Build Consistency",
      key: "consistency",
      icon: <Notes className="w-[24px] h-[24px]" />,
      tabColor: "#FEF0FF",
      value: "Motivate sticking to daily routines and streaks.",
    },
    {
      label: "Build Confidence",
      key: "confidence",
      icon: <YelloPerson className="w-[24px] h-[24px]" />,
      tabColor: "#FDFFF0",
      value: "Quizzes reinforce positive habits and self-belief.",
    },
    {
      label: "Sharpen Focus",
      key: "focus",
      icon: <TargetGreen className="w-[24px] h-[24px]" />,
      tabColor: "#F0FFF1",
      value: "Improve concentration, productivity, and mental clarity.",
    },
  ];

  const handleCardClick = (categoryKey, label) => {
    setSelectedCategory(categoryKey);
    setSelectedCategoryLabel(label);
  };

  const handleCreateQuiz = async () => {
    setIsLoading(true);
    // setIsActionLocked(true);
    startQuizTimer(120);
    try {
      const response = await createQuiz({ category: selectedCategoryLabel });
      setQuizTimeLeft(0);
      if (!isMounted.current) return;
      setTimeout(() => {
        toast.info(t("toast.Quiz created successfully!"));
        navigate("/quiz-game/questionaires", {
          state: { quiz_id: response?.quiz_id },
        });
        setIsLoading(false);
        // setIsActionLocked(false);
        stopQuizTimer();
      }, 2000);
    } catch (error) {
      if (!isMounted.current) return;
      console.log("Failed to create a quiz .Please try again later.", error);
      toast.error(t("toast.Failed to create a quiz .Please try again later."));
      stopQuizTimer();
      // setIsActionLocked(false);
    }
  };

  useEffect(() => {
    isMounted.current = true;
    return () => {
      // setIsActionLocked(false);
      stopQuizTimer();
      isMounted.current = false;
    };
  }, []);

  return (
    <>
      <NavigationPrompt when={isLoading} />
      {isLoading ? (
        <ReacreationLoading
          timeLeft={quizTimeLeft}
          data={mindfulnessSlides}
          minuteSet={120}
          loaderHeading={t("quizGameLoader.quizGameProgress")}
          loaderSubheading={t("quizGameLoader.analyzingPlan")}
          type="quiz"
        />
      ) : (
        <div
          className="bg-cover text-white relative rounded-[12px] p-5 xl:px-[150px] xl:py-[70px]"
          style={{ backgroundImage: `url(${BackImg})` }}
        >
          <div className="w-[100%]">
            <h1 className="text-[24px] text-white font-[700] font-[Lato] mb-3 text-center">
              {t("quizGame.Welcome To Your Wellness Quiz")}
            </h1>
            <h2 className="text-[16px] font-[Lato] font-[500] m-0 text-center">
              {t(
                "quizGame.Pick a goal and get personalized questions and tips"
              )}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-5 font-[Lato] gap-10 mt-10">
            {nutrientData.map(
              ({ label, icon, value, key, tabColor }, index) => (
                <div
                  key={index}
                  className={`py-5 px-3 rounded-[17px] bg-white border rounded-[16px] grid grid-cols-1 items-center gap-2 justify-center cursor-pointer  ${
                    selectedCategory === key
                      ? "border-[#64DDFF] border-[5px] shadow-lg"
                      : "border-[#E6E6E6] hover:border-[#64DDFF]"
                  }  
            
            ${
              !isLoading
                ? "transition-all duration-200 hover:scale-105"
                : "!cursor-default"
            }
            `}
                  style={{
                    boxShadow:
                      selectedCategory === key
                        ? "0 0 15px rgba(100, 221, 255, 0.3)"
                        : "7px 7px 9.5px 0px #0000000D",
                  }}
                  onClick={() => (isLoading ? "" : handleCardClick(key, label))}
                >
                  <div className="w-full flex justify-center">
                    <div
                      className="rounded-full w-11 h-11 flex items-center justify-center"
                      style={{
                        backgroundColor: tabColor,
                      }}
                    >
                      {icon}
                    </div>
                  </div>
                  <div>
                    <p className="text-[18px] font-[500] font-[Lato] text-[#6C6A6A] text-center">
                      {/* {label} */}
                      {t(`quizGame.${key}.label`)}
                    </p>
                    <div className="">
                      <p className="text-[14px] font-[500] font-[Lato] text-[#898787] text-center">
                        {/* {value} */}
                        {t(`quizGame.${key}.value`)}
                      </p>
                    </div>
                  </div>
                  {/* {selectedCategory === key && (
              <div className="absolute top-2 right-2">
                <div className="w-6 h-6 bg-[#64DDFF] rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
            )} */}
                </div>
              )
            )}
          </div>
          <div className="flex justify-center mt-2 md:mt-5 mb-10">
            <div
              className={`py-5 px-3 rounded-[17px] bg-white border rounded-[16px] grid grid-cols-1 items-center gap-2 justify-center cursor-pointer  ${
                selectedCategory === "weightLoss"
                  ? "border-[#64DDFF] border-[5px] shadow-lg"
                  : "border-[#E6E6E6] hover:border-[#64DDFF]"
              }  
            
            ${
              !isLoading
                ? "transition-all duration-200 hover:scale-105"
                : "!cursor-default"
            }
            `}
              style={{
                boxShadow:
                  selectedCategory === "weightLoss"
                    ? "0 0 15px rgba(100, 221, 255, 0.3)"
                    : "7px 7px 9.5px 0px #0000000D",
              }}
              onClick={() =>
                isLoading
                  ? ""
                  : handleCardClick("weightLoss", "Weight Loss Wisdom")
              }
            >
              <div className="w-full flex justify-center">
                <div
                  className="rounded-full w-11 h-11 flex items-center justify-center"
                  style={{
                    backgroundColor: "#FFF7F0",
                  }}
                >
                  <OrangeApple />
                </div>
              </div>
              <div>
                <p className="text-[18px] font-[500] font-[Lato] text-[#6C6A6A] text-center">
                  {t("quizGame.Weight Loss Wisdom")}
                </p>
                <div className="">
                  <p className="text-[14px] font-[500] font-[Lato] text-[#898787] text-center">
                    {t(
                      "quizGame.Test your knowledge on smart, healthy weight loss habits."
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-col-reverse md:flex-row justify-center items-center gap-3">
            <button
              className="bg-[#64DDFF] py-[8px] px-6 text-[20px] font-[600] font-[Lato] text-white rounded-lg flex items-center min-h-[60px] gap-2"
              onClick={() => navigate("/quiz-game")}
            >
              {t("quizGame.Return To Dashboard")}
              <ReturnDashboard />
            </button>
            <button
              className={`py-[8px] px-[32px] text-[20px] font-[600] font-[Lato] text-white rounded-lg transition-all duration-200 min-h-[60px] ${
                selectedCategory
                  ? "bg-[#4CFF7F] hover:bg-[#45E072] animate-pulse hover:animate-none"
                  : "bg-gray-400 cursor-not-allowed"
              }`}
              onClick={handleCreateQuiz}
              disabled={!selectedCategory || isLoading}
            >
              {isLoading ? (
                t("quizGame.We just need a minute to complete it")
              ) : 
                <div className="w-full flex justify-center gap-2 items-center">
                  {t("quizGame.Start Quiz")}
                  <Joystick />
                </div>
              }
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default StartQuizGame;
